import {
  type Background,
  type ColumnLookExtra,
  columnLook,
  type Look,
  type LookStyle,
} from '@/entities/appearance';
import type { Column, ColumnAppearance, ColumnLookInput } from '@/entities/board';

const COLUMN_STYLES: readonly LookStyle[] = [
  'header',
  'border',
  'accent',
  'soft',
  'solid',
  'gradient',
  'pattern',
];
export { COLUMN_STYLES };

/** The column's own look (color + style + extras) as one {@link Look} for the editor. */
export function lookOfColumn(column: Column): Look | null {
  return columnLook(column.color, column.appearance_mode, column.appearance);
}

/** Explicit text colors of a column and its description (all `null` = automatic). */
export interface ColumnTextColors {
  title_color: string | null;
  text_color: string | null;
  description_color: string | null;
  description_surface: string | null;
  form_background: string | null;
  form_text: string | null;
  form_border: string | null;
}

export const NO_TEXT_COLORS: ColumnTextColors = {
  title_color: null,
  text_color: null,
  description_color: null,
  description_surface: null,
  form_background: null,
  form_text: null,
  form_border: null,
};

export function textOfColumn(column: Column): ColumnTextColors {
  const extra = column.appearance;
  return {
    title_color: extra?.title_color ?? null,
    text_color: extra?.text_color ?? null,
    description_color: extra?.description_color ?? null,
    description_surface: extra?.description_surface ?? null,
    form_background: extra?.form_background ?? null,
    form_text: extra?.form_text ?? null,
    form_border: extra?.form_border ?? null,
  };
}

const hasText = (text: ColumnTextColors) => Object.values(text).some((value) => value !== null);

/** Back from the editor's look (+ background) to what `PUT /columns/{id}/appearance` takes. */
export function columnInput(
  look: Look | null,
  background: Background | null,
  text: ColumnTextColors = NO_TEXT_COLORS,
): ColumnLookInput {
  const painted = look !== null && look.style !== 'standard';
  if (!painted && background === null && !hasText(text)) {
    return { color: null, appearance_mode: 'accent', extra: null };
  }
  const extra: ColumnLookExtra = {
    v: 1,
    one_color: painted ? look.one_color : true,
    advanced: painted ? look.advanced : null,
    gradient: painted && look.style === 'gradient' ? look.gradient : null,
    pattern: painted && look.style === 'pattern' ? look.pattern : null,
    background,
    ...(hasText(text) && text),
  };
  return {
    color: painted ? look.color : null,
    appearance_mode: painted ? (look.style as ColumnAppearance) : 'accent',
    extra,
  };
}
